import { Component, Input, QueryList, ViewChildren, forwardRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { firstValueFrom, Observable } from 'rxjs';
import { map, startWith, debounceTime } from 'rxjs/operators';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

@Component({
  selector: 'app-custom-autocomplete',
  templateUrl: './custom-autocomplete.component.html',
  styleUrls: ['./custom-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomAutocompleteComponent),
      multi: true,
    }
  ]
})
export class CustomAutocompleteComponent implements ControlValueAccessor {
  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  @ViewChildren("checkboxes") checkboxes: QueryList<MatCheckbox>;
  @ViewChild(CdkVirtualScrollViewport, { static: false }) virtualScroll: CdkVirtualScrollViewport;

  @Input() list: any[] = [];
  @Input() displayKey: string;
  @Input() valueKey: string;
  @Input() label: string;
  @Input() chips: boolean = false;
  @Input() datacy: string;
  @Input() loading: boolean = false;
  @Input() enableVirtualScroll: boolean = false;

  @Input() singleSelect: boolean = false;

  public filteredItems: Observable<any[]>;
  public noResults: boolean = false;

  public control = new FormControl();

  public lastFilter: string = '';

  private innerValue: any[] = [];
  get value() {
    return this.innerValue;
  }
  set value(v: any[]) {
    if (v !== this.innerValue) {
      this.innerValue = v.map(x => (typeof x === 'object' ? x[this.valueKey] : x));
      this.innerSelectedItems = this.list.filter(item => v.includes(item[this.valueKey]));
      this.onChanged(this.innerValue);
    }
  }

  private innerSelectedItems: any[] = [];
  get selectedItems() {
    return this.innerSelectedItems
  }
  set selectedItems(v: any[]) {
    this.value = v
    this.innerSelectedItems = v
  }

  onChanged: (value: any) => void = () => { };
  onTouched: (value: any) => void = () => { };

  ngOnInit() {
    this.filteredItems = this.control.valueChanges.pipe(
      startWith<string | any[]>(''),
      debounceTime(300),
      map(value => typeof value === 'string' ? value : this.lastFilter),
      map(filter => this.filter(filter)
        .sort((a, b) => {
          if (this.itemSelected(a) && !this.itemSelected(b)) {
            return -1;
          } else if (!this.itemSelected(a) && this.itemSelected(b)) {
            return 1;
          } else {
            return 0;
          }
        }
        )

      ));
  }

  writeValue(obj: any): void {
    if (Array.isArray(obj)) {
      this.value = obj;
    }
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  setDisabledState?(isDisabled: boolean): void {

  }

  renderVirtualViewport() {
    if (this.virtualScroll) {
      setTimeout(() => {
        this.virtualScroll.checkViewportSize();
      }, 50); // Waits for Popover to show up
    }
  }

  public getDisplay(item: any): string {
    if (typeof item === "object") {

      return item[this.displayKey]
    }

    return item
  }

  public getValue(item: any): string {
    if (typeof item === "object") {
      return item[this.valueKey]
    }

    return item
  }

  private filter(filter: string): any[] {

    this.noResults = false

    this.lastFilter = filter;
    //this.page = 0;

    if (filter) {
      let results = this.list.filter(option => option[this.displayKey].toLowerCase().indexOf(filter.toLowerCase()) >= 0)

      if (results.length == 0) {
        this.noResults = true
      }

      return results;
    } else {
      return this.list;
    }
  }

  public toggleSelection(item: any, checked: boolean): void {
    if (this.singleSelect) {

      if (checked) {
        this.value = [item];
        this.selectedItems = [item];
      } else {
        this.value = [];
        this.selectedItems = [];
      }

      return;
    }

    if (checked) {
      this.selectedItems.push(item);
    } else {
      this.selectedItems = this.selectedItems.filter(x => x !== item)
    }

    this.value = this.selectedItems.map(x => x[this.valueKey]);
  }

  public toggleAll(event: MatCheckboxChange): void {
    if (event instanceof MatCheckboxChange) {
      if (event.checked) {
        this.selectedItems = this.list
      }

      if (!event.checked) {
        this.selectedItems = []
      }
    }
  }

  public remove(item: any): void {
    this.selectedItems = this.selectedItems.filter(x => x[this.valueKey] !== item[this.valueKey]);
    this.value = this.selectedItems.map(x => x[this.valueKey]);
  }

  public itemSelected(item: any): boolean {
    return this.selectedItems.includes(item)
  }

  public allItemsSelected(): boolean {
    return this.selectedItems.length == this.list.length
  }

  public displaySelectedItems(): string {
    if (this.allItemsSelected() && !this.singleSelect) {
      return "Todos"
    }

    return this.selectedItems.map(x => typeof x === "string" ? x : x[this.displayKey]).join(", ")
  }

  public handleOpenPanel(event: Event, trigger: MatAutocompleteTrigger) {
    event.stopPropagation();

    if (this.loading) {
      return;
    }

    /*
      Emit event to load filtered items in case network latency had not fetched itens
      when running NgOnInit
    */
    this.control.updateValueAndValidity({emitEvent: true})
    this.renderVirtualViewport()
    setTimeout(() => {
      trigger.openPanel();
    })
  }
  optionClicked(event: Event, item: any) {
    event.stopPropagation();
    this.toggleSelection(item, !this.itemSelected(item));
  }


  public resetFormValues() {
    this.control.reset();
    this.value = [];
    this.selectedItems = [];
  }


  public returnDataCy(validation: string): string {
    return this.datacy + "-" + validation
  }

}
